(function () {
  'use strict';

  /*
    Billiard glove cards: a three-axis configurator.

    WHY THE DATA COMES FROM A FETCH AND NOT FROM LIQUID.

    Every hand/size/colour combination is its own PRODUCT here, not a
    variant - a Kamui glove in Left/Black/M is a separate product from
    Left/Black/L. That makes the collections large: Kamui holds 66 products
    and Liquid reads only the first 50 of any collection, silently. A
    Liquid-rendered matrix would therefore be missing sixteen Kamui
    products with nothing to show for it. So Liquid renders the card's
    skeleton and this file builds the matrix from
    /collections/<handle>/products.json, paged at 250.

    THE THREE AXES, and why all six cards have all three.

    The brief expected a colour axis only on Kamui, Taom and Longoni. The
    catalogue disagrees - measured across all six collections, every line
    carries four values on that axis:

      Kamui      Black, Blue, Red, Pink
      Predator   Black w/Grey Logo, Black w/Yellow Logo,
                 Pink w/Black Logo, Purple w/Black Logo
      Taom       (base Midas), Vivid Cerise, Lime Yellow, Yale Blue
      Longoni    Sultan 3.0, Black Fire 3.0          <- a LINE, not a colour
      McDermott  Black, Blue, Pink, Purple
      Molinari   Black, Cyan, Red, Royal Blue

    Which matters for more than the rail: without that axis, "Left + M" on
    McDermott matches Black, Blue, Pink and Purple at once, and the
    one-match guard the rows are built on has nothing to stand on.

    TITLE SHAPE. One parser covers all six:

      <brand words> Billiard Glove [<line>] for <Left|Right> Hand [<colour>] <size>

    The size is always the last whitespace-delimited token, which is what
    lets "Black w/Grey Logo L/XL" and "Royal Blue XL" and a bare "L" all
    read correctly - the slash inside S/M and L/XL never separates tokens.
    Whatever is left between "Hand" and the size is the colour, and an
    empty remainder is a real value: it is Taom's base Midas.
  */

  var SIZE_LADDER = ['XXS', 'XS', 'S', 'S/M', 'M', 'M/L', 'L', 'L/XL', 'XL', 'XXL'];
  var HANDS = ['Left', 'Right'];
  // Versioned: entries cached before the gallery was carried hold only the
  // featured image, and swatch_image_match has nothing to search in them.
  // Bumped to 3 on 2026-09-23: entries cached before this carry no vendor,
  // and the Longoni card reads it to keep Vaula out.
  var CACHE_PREFIX = 'bc-gloves3:';
  var FETCH_GAP_MS = 350;   // Cloudflare rate-limits a burst; these are serialised
  /*
    A PAGE THAT FAILS IS ASKED AGAIN (2026-10-17, the owner's "three cards
    render empty"). Every card in this row is built from a storefront JSON
    feed, and the edge throttles a burst of them: one refused page used to
    set data-glove-error and stop for the life of the page, so the card stood
    as a brand name over an empty frame - no photo, no price, no swatches, no
    BUY - and because the cards are fetched one after another, a throttle that
    starts mid-chain takes the LATER cards, which is why the owner saw the
    first three whole and the next three empty. Three attempts a page, 700ms
    then 1800ms apart, and the gap between cards is doubled after any retry so
    the chain backs off as a whole rather than hammering the same limit.
  */
  var FETCH_TRIES = 3;
  var FETCH_BACKOFF_MS = [700, 1800];
  var fetchStrain = 0;

  function ladderIndex(size) {
    var i = SIZE_LADDER.indexOf(size);
    return i === -1 ? SIZE_LADDER.length : i;
  }

  /*
    THE LONGONI SHAPE (2026-09-23, the owner's "full range" brief). The rule
    above needs a hand AND a size and drops everything else, which is why 28
    of Longoni's 32 designs were invisible: the shop lists them as separate
    products with neither. Counted on the catalogue, 2026-09-23 -

      Sultan 3.0, Black Fire 3.0   hand and size in the title (10 each)
      Black, Skull 2, Flag 3       hand, no size
      Flag 1, Flag 2               hand, no size, one hand only
      17 designer gloves           neither: "Longoni Billiard Glove Wolf",
                                   "Longoni Renzline Billiard Glove Red/Black"

    So this reads whatever a title carries and says so, instead of refusing
    the title. The design is what is left once the hand, the size and the
    words every glove shares are taken out; a missing hand means the design
    belongs to BOTH hands, and a missing size means it has no size at all -
    neither is guessed.
  */
  var GLOVE_NOISE = /\b(longoni|billiard|gloves?|pool|cue)\b/ig;

  function parseGlove(title) {
    var text = ' ' + String(title || '') + ' ';
    var hand = '';
    var handMatch = text.match(/\bfor\s+(Left|Right)\s+Hand\b/i);
    if (handMatch) {
      hand = handMatch[1].charAt(0).toUpperCase() + handMatch[1].slice(1).toLowerCase();
      text = text.replace(handMatch[0], ' ');
    }

    var size = '';
    var tail = text.trim().split(/\s+/).pop() || '';
    if (tail && ladderIndex(tail.toUpperCase()) !== SIZE_LADDER.length) {
      size = tail.toUpperCase();
      text = text.replace(new RegExp('\\s' + tail.replace(/[.*+?^${}()|[\]\\\/]/g, '\\$&') + '\\s*$'), ' ');
    }

    var design = text.replace(GLOVE_NOISE, ' ').replace(/\s{2,}/g, ' ').trim();
    if (!design) return null;
    return { design: design, hand: hand, size: size };
  }

  /*
    Design-major, for a card whose products are one design each rather than
    one colour-and-size each. The matrix keeps the shape the rest of this
    file reads - value -> hand -> size -> product - so the hand row, the
    reserve, the hover preview and the commit are the same code:
      * a product with no hand is written under BOTH hands, flagged, so the
        caption can leave the hand out;
      * a product with no size takes the empty key, which sorts last and
        renders no size pill.
  */
  /*
    The two title filters a card may carry, shared by both matrices
    (2026-09-23): "include" keeps only the titles that carry one of its
    tokens, "exclude" drops any title that carries one. They are what split
    one collection between two cards - Longoni's four cue-room gloves and
    its seventeen designs - without a second collection in the admin.
  */
  function tokensOf(card, name) {
    return (card.getAttribute(name) || '').split('|')
      .map(function (s) { return s.trim(); })
      .filter(function (s) { return s.length; });
  }

  function titleFilter(card) {
    var include = tokensOf(card, 'data-include');
    var exclude = tokensOf(card, 'data-exclude');
    return function (title) {
      var t = String(title || '');
      if (include.length && !include.some(function (tok) { return t.indexOf(tok) !== -1; })) return false;
      if (exclude.length && exclude.some(function (tok) { return t.indexOf(tok) !== -1; })) return false;
      return true;
    };
  }

  function buildDesignMatrix(products, keep) {
    var matrix = {};
    products.forEach(function (product) {
      if (keep && !keep(product.title)) return;
      if (/vaula/i.test(product.vendor || '')) return;   // Vaula has a card of its own
      var parsed = parseGlove(product.title);
      if (!parsed) return;
      var entry = {};
      for (var key in product) { if (Object.prototype.hasOwnProperty.call(product, key)) entry[key] = product[key]; }
      entry.handless = !parsed.hand;
      var hands = parsed.hand ? [parsed.hand] : HANDS;
      hands.forEach(function (hand) {
        if (!matrix[parsed.design]) matrix[parsed.design] = {};
        if (!matrix[parsed.design][hand]) matrix[parsed.design][hand] = {};
        matrix[parsed.design][hand][parsed.size] =
          betterOf(matrix[parsed.design][hand][parsed.size], entry);
      });
    });
    return matrix;
  }

  function parseTitle(title) {
    var m = title.match(/^(.*?)\s+for\s+(Left|Right)\s+Hand\s*(.*)$/i);
    if (!m) return null;
    var head = m[1].trim();
    var hand = m[2].charAt(0).toUpperCase() + m[2].slice(1).toLowerCase();
    var tail = m[3].trim();
    if (!tail) return null;

    var parts = tail.split(/\s+/);
    var size = parts.pop();
    var colour = parts.join(' ').trim();

    /*
      The line is whatever the head carries after "Billiard Glove" - Kamui's
      QuickDry, Longoni's Sultan 3.0. Empty for the brands that name no
      line, which is most of them.
    */
    var line = '';
    var lineMatch = head.match(/Billiard\s+Glove\s+(.+)$/i);
    if (lineMatch) line = lineMatch[1].trim();

    return { hand: hand, size: size, colour: colour, line: line };
  }

  function fetchCollection(handle) {
    var key = CACHE_PREFIX + handle;
    try {
      var cached = window.sessionStorage.getItem(key);
      if (cached) return Promise.resolve(JSON.parse(cached));
    } catch (error) { /* private mode: just fetch */ }

    var collected = [];
    function wait(ms) {
      return new Promise(function (resolve) { window.setTimeout(resolve, ms); });
    }
    // One page, tried up to FETCH_TRIES times. A refusal and a dropped
    // connection are the same thing here - both mean "ask again in a moment".
    function pageOnce(n, attempt) {
      return fetch('/collections/' + handle + '/products.json?limit=250&page=' + n, { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        })
        .catch(function (error) {
          if (attempt + 1 >= FETCH_TRIES) throw error;
          fetchStrain += 1;
          return wait(FETCH_BACKOFF_MS[Math.min(attempt, FETCH_BACKOFF_MS.length - 1)])
            .then(function () { return pageOnce(n, attempt + 1); });
        });
    }
    function page(n) {
      return pageOnce(n, 0)
        .then(function (body) {
          var products = (body && body.products) || [];
          collected = collected.concat(products);
          if (products.length < 250) return collected;
          return wait(FETCH_GAP_MS).then(function () { return page(n + 1); });
        });
    }
    return page(1).then(function (products) {
      var slim = products.map(function (p) {
        var v = (p.variants && p.variants[0]) || {};
        return {
          title: p.title,
          vendor: p.vendor || '',
          url: ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'products/' + p.handle,
          price: v.price,
          available: !!v.available,
          image: (p.images && p.images[0] && p.images[0].src) || '',
          // the whole gallery, so swatch_image_match has something to search
          images: (p.images || []).map(function (i) { return i.src; })
        };
      });
      try { window.sessionStorage.setItem(key, JSON.stringify(slim)); } catch (error) {}
      return slim;
    });
  }

  /*
    Two products can land on the same colour/hand/size cell. Kamui has
    twenty such pairs - the same glove listed twice, at $25.50 and at
    $29.40 - and the two copies do not carry the same stock: Black Left S
    is in stock on the cheap copy and sold out on the dear one. Taking
    whichever came last in the feed therefore drops sizes off the rail that
    the shop can actually ship, which is what it did to that S.

    So the cell resolves rather than overwrites: in stock beats sold out,
    and between two of the same it is the cheaper one, which is both the
    right offer to show and a rule that does not depend on feed order.
  */
  function betterOf(current, candidate) {
    if (!current) return candidate;
    if (current.available !== candidate.available) {
      return current.available ? current : candidate;
    }
    var a = parseFloat(current.price);
    var b = parseFloat(candidate.price);
    if (isFinite(a) && isFinite(b) && b < a) return candidate;
    return current;
  }

  function designLayout(card) {
    return card.getAttribute('data-layout') === 'design';
  }

  function buildMatrix(card, products) {
    var keep = titleFilter(card);
    if (designLayout(card)) return buildDesignMatrix(products, keep);

    var axis1Field = card.getAttribute('data-axis1') === 'line' ? 'line' : 'colour';

    var matrix = {};   // axis1 -> hand -> size -> product
    products.forEach(function (product) {
      if (!keep(product.title)) return;
      var parsed = parseTitle(product.title);
      if (!parsed) return;
      if (ladderIndex(parsed.size) === SIZE_LADDER.length) return;   // not a size we ladder

      var axis1 = parsed[axis1Field];
      if (!matrix[axis1]) matrix[axis1] = {};
      if (!matrix[axis1][parsed.hand]) matrix[axis1][parsed.hand] = {};
      matrix[axis1][parsed.hand][parsed.size] =
        betterOf(matrix[axis1][parsed.hand][parsed.size], product);
    });
    return matrix;
  }

  function inStockSizes(matrix, axis1, hand) {
    var bucket = (matrix[axis1] && matrix[axis1][hand]) || {};
    return Object.keys(bucket)
      .filter(function (size) { return bucket[size].available; })
      .sort(function (a, b) { return ladderIndex(a) - ladderIndex(b); });
  }

  /*
    The card opens on the FIRST swatch of its grid.

    It used to open on M - S/M where there was no M, then the nearest larger
    size - which reads well as a size recommendation but does not survive
    contact with the grid: the swatches run colour-major with the sizes in
    ladder order, so M sits partway along the first colour's run and the
    green frame landed in the middle of row one. Measured on the row as
    shipped: Kamui opened on its third swatch, Predator on its second.

    inStockSizes already returns the ladder in ascending order, so the first
    swatch of a colour's run is simply its first entry, and the grid's very
    first swatch is that of the colour flowOrder puts first.
  */

  function axis1Values(card, matrix) {
    var order = (card.getAttribute('data-axis1-order') || '').split('|')
      .map(function (s) { return s.trim(); });
    var present = Object.keys(matrix).filter(function (value) {
      // a value survives only while one of its hands still has stock
      return HANDS.some(function (hand) { return inStockSizes(matrix, value, hand).length; });
    });
    if (!order.length || (order.length === 1 && !order[0])) return present.sort();
    var ordered = order.filter(function (value) { return present.indexOf(value) !== -1; });
    present.forEach(function (value) { if (ordered.indexOf(value) === -1) ordered.push(value); });
    return ordered;
  }

  /*
    The colourless value is a real one - Taom's base Midas carries no
    colour word at all - so it needs a name. It comes from the section
    settings rather than from a string here, because it is user-facing
    text and this file has no locale.
  */
  function labelFor(card, value) {
    if (value !== '') return value;
    return card.getAttribute('data-base-label') || '';
  }

  function chip(label, active, kind) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'simonis-family-card__size-option glove-card__chip glove-card__chip--' + kind + (active ? ' is-active' : '');
    button.setAttribute('role', 'tab');
    button.setAttribute('aria-selected', active ? 'true' : 'false');
    button.textContent = label;
    return button;
  }

  /*
    The photo swap is the shared atomic pattern: decode first, then write
    src, caption, price and href together inside one animation frame, so a
    card never shows the new price against the old picture. A token
    discards a decode that lands after a newer choice was made.
  */
  // Shared hover mechanics - the decode gate, the staleness token and the
  // group-wide leave rule. See assets/bc-hover-preview.js.
  var HP = window.BCHoverPreview || null;

  // One place composes the line under the grid, so the hover preview and the
  // click that follows it cannot word the same combination differently.
  function captionFor(card, value, hand, size, product) {
    // A design the shop does not sell per hand - most of Longoni's - says
    // nothing about hands rather than claiming one.
    var handPart = product && product.handless ? '' : hand;
    return [labelFor(card, value), handPart, size].filter(Boolean).join(' · ');
  }

  var swapTokens = new WeakMap();
  function commit(card, state) {
    var product = state.product;
    var image = card.querySelector('[data-glove-image]');
    var token = (swapTokens.get(card) || 0) + 1;
    swapTokens.set(card, token);
    // A choice must also cancel a hover decode still in flight. The two
    // paths count in different places, so the shared counter is bumped here
    // as well - otherwise a slow preview lands after the click and the card
    // ends up showing a combination nobody selected.
    if (HP) HP.nextToken(card);
    var wanted = sized(pickImage(product, matchNeedle(card)), 600);

    var apply = function () {
      if (swapTokens.get(card) !== token) return;
      window.requestAnimationFrame(function () {
        if (swapTokens.get(card) !== token) return;
        if (image && wanted) image.src = wanted;
        var price = card.querySelector('[data-glove-price]');
        if (price) price.textContent = formatMoney(product.price);
        var caption = card.querySelector('[data-glove-caption]');
        if (caption) caption.textContent = captionFor(card, state.axis1, state.hand, state.size, product);
        var buy = card.querySelector('[data-glove-buy]');
        if (buy) {
          buy.href = product.url;
          buy.textContent = buy.getAttribute('data-buy-label') || 'BUY';
        }
      });
    };

    // The probe asks for the same url the swap writes - a different one
    // would warm one cache entry and leave the img fetching another.
    if (image && wanted && image.getAttribute('src') !== wanted) {
      var probe = new Image();
      probe.src = wanted;
      if (probe.decode) probe.decode().then(apply, apply);
      else { probe.onload = apply; probe.onerror = apply; }
      window.setTimeout(apply, 800);   // relief valve, as elsewhere in the theme
    } else {
      apply();
    }
  }

  function formatMoney(cents) {
    if (cents === null || cents === undefined) return '';
    var n = Number(cents);
    if (isNaN(n)) return String(cents);
    // products.json gives a decimal string like "25.50", not cents
    return '$' + n.toFixed(2);
  }

  /*
    Which of a product's photos the card draws.

    The featured image cannot be the answer everywhere. McDermott's and
    Longoni's left-hand gloves are featured on the manufacturer's close
    lifestyle frame, where a bare hand fills the picture and the glove is a
    small part of it; at 40px that swatch is a skin-coloured blob. The
    featured image is not ours to change either - it feeds the collection
    pages, the OG previews and the product feeds - so the card picks a
    different one from the same gallery.

    IT IS A SUBSTRING OF THE FILE PATH, NOT AN INDEX, for the reason the
    McDermott tiles take band_image_match the same way: a gallery gets
    reordered in admin and every index silently points at a new picture,
    while "_02" still finds the photo it always found. The query string is
    cut before the search so a needle can never match ?v= or the &width=
    this file appends.

    No needle, or no photo matching it, and the product keeps its featured
    image - exactly the behaviour before this setting existed.
  */
  function pickImage(product, needle) {
    if (!needle) return product.image;
    var gallery = product.images || [];
    for (var i = 0; i < gallery.length; i++) {
      if (gallery[i].split('?')[0].indexOf(needle) !== -1) return gallery[i];
    }
    return product.image;
  }

  function matchNeedle(card) {
    return card.getAttribute('data-swatch-image-match') || '';
  }

  /*
    The products.json feed hands back the original upload - 3000px square
    at McDermott - and a swatch draws it at 40. Shopify's CDN resizes on
    the query string, so ask it for the size actually painted rather than
    pulling megabytes per swatch. Anything that is not a CDN url is left
    alone.
  */
  function sized(url, width) {
    // products.json returns the cdn.shopify.com host, not the storefront's
    // own /cdn/ path, so both spellings have to be recognised.
    if (!url) return url;
    if (url.indexOf('cdn.shopify.com') === -1 && url.indexOf('/cdn/') === -1) return url;
    if (url.indexOf('width=') !== -1) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /*
    THE SIZE UNDER THE SWATCH (2026-09-14). The Van Ooy feed carries no size
    option - every glove is a product of its own with a single "Title"
    option - so the size is read from a variant option named "Size" when a
    product has one, and otherwise from the title token parseTitle already
    takes (XXL, L/XL). The caption is aria-hidden: the button's aria-label
    below already says the size.
  */
  function sizeCaption(product, parsedSize) {
    var options = (product && product.options) || [];
    var variants = (product && product.variants) || [];
    for (var i = 0; i < options.length; i++) {
      var option = options[i];
      if (!option || !/size/i.test(String(option.name || ''))) continue;
      var variant = variants.filter(function (v) { return v.available; })[0] || variants[0];
      var value = variant && variant['option' + (option.position || i + 1)];
      if (value) return String(value);
    }
    return parsedSize ? String(parsedSize) : '';
  }

  /*
    A bare square thumbnail, the same swatch chalk, cloth and the tips use,
    with the size hanging under it as a caption (.bc-swatch-caption,
    cloth-card-styles). The title and aria-label name it in full - a hover
    tooltip and a screen-reader name.
  */
  function swatch(product, src, name, size, active) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'cloth-size-card__color-option' + (active ? ' is-active' : '');
    button.setAttribute('role', 'option');
    button.setAttribute('aria-selected', active ? 'true' : 'false');

    var label = [name, size].filter(Boolean).join(' ');
    button.title = label;
    button.setAttribute('aria-label', label);

    // A product with no photo still gets its swatch, but an img with an
    // empty src would draw a broken icon.
    if (src) {
      var image = document.createElement('img');
      image.className = 'cloth-size-card__color-thumbnail';
      image.src = sized(src, 96);
      image.alt = '';
      image.width = 44;
      image.height = 44;
      image.loading = 'lazy';
      button.appendChild(image);
    }

    var captionText = sizeCaption(product, size);
    if (captionText) {
      var caption = document.createElement('span');
      caption.className = 'bc-swatch-caption';
      caption.setAttribute('aria-hidden', 'true');
      caption.textContent = captionText;
      button.appendChild(caption);
    }
    return button;
  }

  function handCount(values, matrix, hand) {
    return values.reduce(function (sum, value) {
      return sum + inStockSizes(matrix, value, hand).length;
    }, 0);
  }

  /*
    THE CARD IS CAPPED, NOT SCROLLED (2026-09-23, the owner's second word on
    the Longoni card). One control and one grid, like the other five cards,
    which means every design's every size is a swatch of its own - and
    Longoni has more of those than the card's height holds. The cap is a
    number of SWATCHES, measured from the geometry rather than guessed:

      1280: 5 columns x 4 rows of the reserve that Kamui's grid leaves
      375:  6 columns x 4 rows

    so 24 swatches is the most that fits either width without the card
    growing past the tallest card in the lane. Designs are taken in order
    until the next one would pass it, and the count that decides is the
    WORSE hand's: the grid must not change height when LEFT/RIGHT is
    pressed. What is left out is in the report, for the owner's call on a
    second card.
  */
  function cappedValues(card, matrix, values) {
    var cap = parseInt(card.getAttribute('data-max-swatches'), 10);
    if (!cap || cap < 1) return values;
    var kept = [];
    var used = { Left: 0, Right: 0 };
    values.forEach(function (value) {
      var next = {
        Left: used.Left + inStockSizes(matrix, value, 'Left').length,
        Right: used.Right + inStockSizes(matrix, value, 'Right').length
      };
      if (next.Left > cap || next.Right > cap) return;
      used = next;
      kept.push(value);
    });
    return kept.length ? kept : values.slice(0, 1);
  }

  /*
    THE RESERVE, measured rather than declared.

    Predator has eleven gloves for the left hand and three for the right;
    without a reserve, switching hand would pull the caption and the BUY
    button up two rows under the pointer. The height is written as
    --glove-slack on the CARD, and the stylesheet spends it as padding at
    the foot of the swatch grid, so the unused rows sit between the last
    swatch and the caption. It went on the photo zone instead until
    2026-09-07, which put them above the first swatch and left every
    card's grid starting at a different y.

    On the card rather than on either box because both read it: the
    property has to be visible to the grid, and a sibling cannot see a
    property set on the zone.

    It has to be measured because the column count is no longer a
    constant: auto-fill deals five columns at 1280 and six at 1440, and a
    row is as tall as a column is wide. So the resolved track list and one
    swatch's rendered height are read back, and the rows the other hand
    would take are worked out from those. Re-run on resize, since the
    count changes with the card.
  */
  function reserve(card) {
    var grid = card.querySelector('[data-glove-swatches]');
    var counts = card.__gloveCounts;
    if (!grid || !counts) return;
    var style = window.getComputedStyle(grid);
    var cols = (style.gridTemplateColumns.match(/[\d.]+px/g) || []).length;
    var cell = grid.querySelector('button');
    if (!cols || !cell) return;
    var size = cell.offsetHeight;
    var gap = parseFloat(style.rowGap) || 0;
    var rowsNow = Math.ceil(counts.now / cols);
    var rowsMax = Math.max(
      Math.ceil(counts.Left / cols),
      Math.ceil(counts.Right / cols),
      rowsNow
    );
    card.style.setProperty('--glove-slack', ((rowsMax - rowsNow) * (size + gap)) + 'px');
  }

  var resizeTimer = 0;
  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      Array.prototype.forEach.call(document.querySelectorAll('[data-glove-card][data-glove-ready]'), reserve);
    }, 150);
  });

  /*
    The order the swatches run in: the default colour first, then the rest
    as the settings list them - Kamui's Black, Blue, Red, Pink. It is the
    CONFIGURED default that leads, not the current selection, or every
    click would deal the grid again and move the swatches out from under
    the pointer.
  */
  function flowOrder(card, values) {
    var first = card.getAttribute('data-default-axis1') || '';
    if (values.indexOf(first) === -1) return values;
    return [first].concat(values.filter(function (value) { return value !== first; }));
  }

  function render(card, matrix) {
    var values = cappedValues(card, matrix, flowOrder(card, axis1Values(card, matrix)));
    if (!values.length) { card.hidden = true; return; }

    var state = card.__gloveState || {};

    /*
      Hand is now the card's own axis rather than the chosen colour's: the
      grid below shows whatever that hand stocks, so a hand counts as
      available while ANY colour has stock for it. The cascade survives in
      that form - a line whose left hand is empty end to end still opens on
      the right.
    */
    var hands = HANDS.filter(function (hand) {
      return values.some(function (value) { return inStockSizes(matrix, value, hand).length; });
    });
    if (hands.indexOf(state.hand) === -1) state.hand = hands.indexOf('Left') !== -1 ? 'Left' : hands[0];

    // colours this hand actually stocks, in the configured order
    var handValues = values.filter(function (value) { return inStockSizes(matrix, value, state.hand).length; });
    if (handValues.indexOf(state.axis1) === -1) {
      var wanted = card.getAttribute('data-default-axis1') || '';
      state.axis1 = handValues.indexOf(wanted) !== -1 ? wanted : handValues[0];
    }

    /*
      Keep the size across a LEFT/RIGHT switch when the other hand stocks it,
      otherwise fall to the first of this colour's run. That is the rule the
      chalk cards use when a size tab changes: keep the chosen colour if the
      new panel has it, else take the panel's first option - see
      initializeColorPanel in simonis-family-size-selector.js. On first
      render nothing is chosen yet, so both axes fall through to their first
      entry and the card opens on swatch one.
    */
    var sizes = inStockSizes(matrix, state.axis1, state.hand);
    /* sizes[0] is the EMPTY STRING for a design the shop sells in one size
       only - Longoni's designer gloves - and an empty string is falsy, so
       `|| null` threw that design's own key away and the card then looked
       up matrix[design][hand][null]: no product, no caption, no photo.
       Measured on Wolf, 2026-09-23. */
    if (sizes.indexOf(state.size) === -1) state.size = sizes.length ? sizes[0] : null;

    state.product = matrix[state.axis1][state.hand][state.size];
    card.__gloveState = state;

    var handRow = card.querySelector('[data-glove-hands]');
    var grid = card.querySelector('[data-glove-swatches]');

    /*
      Both hands always stand, and one with nothing in the whole line is
      disabled rather than removed, so the row keeps its height and the
      absence reads as "not sold" rather than as a rail that lost a button.
    */
    handRow.innerHTML = '';
    HANDS.forEach(function (hand) {
      var live = hands.indexOf(hand) !== -1;
      var button = chip(hand, live && hand === state.hand, 'hand');
      if (!live) {
        button.disabled = true;
        button.className += ' is-unavailable';
        handRow.appendChild(button);
        return;
      }
      button.addEventListener('click', function () {
        card.__gloveState.hand = hand;
        render(card, matrix);
      });
      handRow.appendChild(button);
    });

    /*
      One flow, wrapping on the card's width, the way the tips card runs
      its swatches. The grouping lives in the order rather than in the
      markup: every size of the first colour along the ladder, then every
      size of the second, so a colour still reads as a run and no row ends
      in empty cells.
    */
    card.__gloveCounts = {
      Left: handCount(values, matrix, 'Left'),
      Right: handCount(values, matrix, 'Right'),
      now: handCount(values, matrix, state.hand)
    };
    grid.innerHTML = '';
    var needle = matchNeedle(card);

    flowOrder(card, handValues).forEach(function (value) {
      inStockSizes(matrix, value, state.hand).forEach(function (size) {
        var product = matrix[value][state.hand][size];
        var active = value === state.axis1 && size === state.size;
        var photo = pickImage(product, needle);
        var button = swatch(product, photo, labelFor(card, value), size, active);
        /*
          The card-sized frame this swatch previews on hover, written on the
          element because the handler is delegated on the document and has no
          way back into this closure. It is the url commit() would write for
          the same choice - including the swatch_image_match substitution, so
          the preview shows exactly the frame the click would show - which
          also means hovering warms the entry the click then paints.
        */
        var large = sized(photo, 600);
        if (large) button.setAttribute('data-glove-large-src', large);
        /*
          The caption and the price this swatch stands for, written on the
          element for the same reason the photo url is: the hover handler is
          delegated on the document and cannot see this closure. Both are
          composed exactly as commit() composes them for a click, so a
          preview and the choice it previews cannot word the same
          combination differently.
        */
        button.setAttribute('data-glove-caption-text', captionFor(card, value, state.hand, size));
        button.setAttribute('data-glove-price-text', formatMoney(product.price));
        button.addEventListener('click', function () {
          card.__gloveState.axis1 = value;
          card.__gloveState.size = size;
          render(card, matrix);
        });
        grid.appendChild(button);
      });
    });

    card.setAttribute('data-glove-ready', 'true');
    // measured after ready, once the pre-fetch stand-in has let go of the grid
    reserve(card);
    commit(card, state);
  }

  /*
    Hover preview, the mechanic the cloth, chalk and tips rows already use:
    pointing at a swatch shows that combination's photo, and leaving the card
    puts the selected one back.

    This is not a regression from the grid rewrite or the photo work - no
    pointer handler has ever existed in this file, and the section never
    loaded bc-hover-preview.js. The row simply never had the feature.

    THE CAPTION AND THE PRICE COME WITH IT; the BUY link does not.

    That division is the chalk row's, measured rather than assumed - hovering
    a chip on the TAOM card, 2026-09-09: the colour name went "Blue 1 pc" to
    "Blue Box", the price $19.99 to $21.95, and the BUY href did not move.
    Leaving put all three back. cloth-color-previews.js does it in one
    applyOption() for hover, leave and click alike, and only updateBuyLink()
    is held back for the click.

    The reason it is safe to move the price here is the same as there: what
    the pointer is over IS what the card is describing at that moment, and
    the line under the grid is the only thing that says which combination
    that is. Leaving the price behind was what made the size readable only
    through the browser's own tooltip.

    Delegated on the document rather than bound to the grid: render() empties
    the grid and rebuilds every swatch on each LEFT/RIGHT switch, so a
    listener held on those elements would not survive the first switch.
  */
  function gloveSwatch(target) {
    if (!(target instanceof Element) || !target.closest) return null;
    var button = target.closest('[data-glove-swatches] [data-glove-large-src]');
    return button && button.closest('[data-glove-card]') ? button : null;
  }

  // The line under the grid and the figure beside it, together. Written
  // straight rather than through the decode gate: text has nothing to decode,
  // and holding it back until the photo is ready is what would make the two
  // arrive separately.
  function writeGloveText(card, caption, price) {
    var captionEl = card && card.querySelector('[data-glove-caption]');
    var priceEl = card && card.querySelector('[data-glove-price]');
    if (HP) { HP.setText(captionEl, caption); HP.setText(priceEl, price); }
    else {
      if (captionEl && caption) captionEl.textContent = caption;
      if (priceEl && price) priceEl.textContent = price;
    }
  }

  function previewSwatch(button) {
    var card = button.closest('[data-glove-card]');
    if (!card) return;
    writeGloveText(card,
      button.getAttribute('data-glove-caption-text'),
      button.getAttribute('data-glove-price-text'));
    var image = card.querySelector('[data-glove-image]');
    var src = button.getAttribute('data-glove-large-src');
    if (!image || !src) return;
    if (HP) HP.swapImage(image, { src: src, alt: image.alt }, { group: card });
    else image.src = src;
  }

  // Synchronous, like the cloth restore: leaving the card must not be able to
  // strand it on a preview whose decode is still in flight.
  function restoreGloveCard(card) {
    var state = card && card.__gloveState;
    if (!state || !state.product) return;
    // Recomposed from the state rather than read back off the active swatch:
    // the grid is rebuilt on every LEFT/RIGHT switch, and the state is the
    // one thing that survives it.
    writeGloveText(card,
      captionFor(card, state.axis1, state.hand, state.size),
      formatMoney(state.product.price));
    var image = card.querySelector('[data-glove-image]');
    if (!image) return;
    var src = sized(pickImage(state.product, matchNeedle(card)), 600);
    if (!src) return;
    if (HP) HP.swapImage(image, { src: src, alt: image.alt }, { group: card, immediate: true });
    else image.src = src;
  }

  // Hover only where a hover exists. On a phone the tap is a click and the
  // click handler owns the choice, exactly as on the cloth rows.
  document.addEventListener('pointerover', function (event) {
    if (HP && !HP.hoverCapable()) return;
    var button = gloveSwatch(event.target);
    if (!button) return;
    previewSwatch(button);
  });

  // The one revert: the pointer leaves the swatch grid - pointerleave on
  // [data-glove-swatches], served by bc-hover-preview.js for every row.
  // Not a swatch, not the gap between two, not the card (which kept the
  // last swatch hovered on show all the way down to BUY).
  if (HP) {
    HP.onZoneLeave('[data-glove-swatches]', function (zone) {
      restoreGloveCard(zone.closest('[data-glove-card]'));
    });
  }

  // Keyboard parity: tabbing onto a swatch previews it, tabbing off the card
  // restores. The focus a pointer press leaves behind is skipped - the click
  // has already made the choice.
  document.addEventListener('focusin', function (event) {
    var button = gloveSwatch(event.target);
    if (!button || (HP && !HP.focusVisible(button))) return;
    previewSwatch(button);
  });

  document.addEventListener('focusout', function (event) {
    var button = gloveSwatch(event.target);
    if (!button || (HP && !HP.focusVisible(button))) return;
    if (HP && !HP.leavesGroup(event.target, event.relatedTarget)) return;
    restoreGloveCard(button.closest('[data-glove-card]'));
  });

  function start() {
    var cards = Array.prototype.slice.call(document.querySelectorAll('[data-glove-card]'));
    if (!cards.length) return;
    // A card hidden by a failed feed earlier gets its frame back for this pass.
    cards.forEach(function (card) {
      if (card.getAttribute('data-glove-error')) {
        card.removeAttribute('data-glove-error');
        card.hidden = false;
      }
    });

    // Serialised, with a gap: six collections in a burst is what the edge
    // rate-limits, and nothing here is urgent enough to race.
    cards.reduce(function (chain, card) {
      return chain.then(function () {
        var handle = card.getAttribute('data-collection');
        if (!handle) return null;
        return fetchCollection(handle)
          .then(function (products) { render(card, buildMatrix(card, products)); })
          .catch(function () {
            /*
              Every retry is spent and the feed still will not come. An empty
              frame with a brand name on it is worse than one card fewer, and
              render() already hides a card whose collection holds nothing in
              stock, so the same thing happens here. The flag stays for the
              next start() - shopify:section:load re-runs this - to find.
            */
            card.setAttribute('data-glove-error', 'true');
            card.hidden = true;
          })
          .then(function () {
            // after a retry the edge is already unhappy: give it longer
            return new Promise(function (resolve) {
              window.setTimeout(resolve, FETCH_GAP_MS * (fetchStrain ? 3 : 1));
            });
          });
      });
    }, Promise.resolve());
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
